/**
 * F17.2.12 — Lista stratigrafie Controsoffitto (le mie creazioni salvate).
 *
 * Versione semplificata di StratigraphyListV2 con:
 *  - Hook `useUnifiedStratigraphies('ceiling')` (filtra automaticamente per type)
 *  - Card click → `/ceilings/configurator?id=<uuid>` (NON `/configurator?id=` come wall)
 *  - Niente confronto/wizard per ora (arriveranno con F17.2.15)
 *  - Niente filtri tecnici complessi (search testo + certificata/personalizzata)
 *
 * Riusa `StratigraphyCardV2` per mantenere coerenza visiva con la lista wall.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, PanelTop, Star } from 'lucide-react';
import { useUnifiedStratigraphies } from '@/hooks/useUnifiedStratigraphies';
import { useFavoriteStratigraphies } from '@/hooks/useFavoriteStratigraphies';
import StratigraphyCardV2 from './StratigraphyCardV2';

const CeilingStratigraphyList: React.FC = () => {
  const navigate = useNavigate();
  const [showFavoritesOnly, setShowFavoritesOnly] = React.useState(false);
  const { favoritesSet } = useFavoriteStratigraphies();
  const {
    stratigraphies,
    isLoading,
    totalCount,
    certifiedCount,
    customCount,
    searchTerm,
    setSearchTerm,
    fireResistance,
    setFireResistance,
    acousticReduction,
    setAcousticReduction,
    supplierName,
    setSupplierName,
    availableSuppliers,
    thicknessRange,
    setThicknessRange,
    weightRange,
    setWeightRange,
    showCertifiedOnly,
    setShowCertifiedOnly,
    showCustomOnly,
    setShowCustomOnly,
  } = useUnifiedStratigraphies('ceiling');

  const openCeiling = (id: string) => {
    navigate(`/ceilings/configurator?id=${id}`);
  };

  if (isLoading) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2" />
        Caricamento controsoffitti…
      </Card>
    );
  }

  // Caso: org senza nessun ceiling salvato → CTA pulita
  if (totalCount === 0) {
    return (
      <Card className="p-8 text-center border-dashed">
        <PanelTop className="h-10 w-10 mx-auto mb-2 text-zinc-300" />
        <p className="text-sm font-medium text-zinc-700 mb-1">
          Nessun controsoffitto salvato
        </p>
        <p className="text-xs text-zinc-500">
          Crea il tuo primo controsoffitto partendo da una tipologia standard qui sopra
          o cliccando &quot;Nuovo controsoffitto&quot; in alto a destra.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* === FILTRI compatti === */}
      <Card>
        <CardContent className="pt-4 pb-3 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
              Cerca per nome o codice
            </Label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                placeholder="es. CS-FIRE-60, sala riunioni…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 text-sm pl-8"
              />
            </div>
          </div>

          {/* Filtri tecnici — allineati al wall */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                Resistenza al fuoco
              </Label>
              <Select value={fireResistance || 'all'} onValueChange={setFireResistance}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="EI 30">EI 30</SelectItem>
                  <SelectItem value="EI 60">EI 60</SelectItem>
                  <SelectItem value="EI 90">EI 90</SelectItem>
                  <SelectItem value="EI 120">EI 120</SelectItem>
                  <SelectItem value="EI 180">EI 180</SelectItem>
                  <SelectItem value="EI 240">EI 240</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                Abbattimento acustico
              </Label>
              <Select value={acousticReduction || 'all'} onValueChange={setAcousticReduction}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="30">≥ 30 dB</SelectItem>
                  <SelectItem value="40">≥ 40 dB</SelectItem>
                  <SelectItem value="50">≥ 50 dB</SelectItem>
                  <SelectItem value="60">≥ 60 dB</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                Spessore pacchetto
              </Label>
              <Select value={thicknessRange || 'all'} onValueChange={setThicknessRange}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="0-50">0-50 mm</SelectItem>
                  <SelectItem value="51-100">51-100 mm</SelectItem>
                  <SelectItem value="101-200">101-200 mm</SelectItem>
                  <SelectItem value="201-300">201-300 mm</SelectItem>
                  <SelectItem value="300+">300+ mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                Fornitore
              </Label>
              {availableSuppliers && availableSuppliers.length > 0 ? (
                <Select
                  value={supplierName || 'all'}
                  onValueChange={(v) => setSupplierName(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Tutti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti</SelectItem>
                    {availableSuppliers.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder="es. Saint Gobain..."
                  value={supplierName}
                  onChange={(e) => setSupplierName(e.target.value)}
                  className="h-9 text-sm"
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">
                Peso (kg/m²)
              </Label>
              <Select value={weightRange || 'all'} onValueChange={setWeightRange}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="0-15">leggero (&lt; 15 kg/m²)</SelectItem>
                  <SelectItem value="15-25">medio (15-25)</SelectItem>
                  <SelectItem value="25-40">pesante (25-40)</SelectItem>
                  <SelectItem value="40+">extra-pesante (40+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            <Badge
              variant={!showCertifiedOnly && !showCustomOnly ? 'default' : 'outline'}
              className="cursor-pointer text-xs px-2.5 py-0.5"
              onClick={() => {
                setShowCertifiedOnly(false);
                setShowCustomOnly(false);
              }}
            >
              Tutti ({totalCount})
            </Badge>
            <Badge
              variant={showCertifiedOnly ? 'default' : 'outline'}
              className="cursor-pointer text-xs px-2.5 py-0.5"
              onClick={() => {
                setShowCertifiedOnly(!showCertifiedOnly);
                setShowCustomOnly(false);
              }}
            >
              Certificati ({certifiedCount})
            </Badge>
            <Badge
              variant={showCustomOnly ? 'default' : 'outline'}
              className="cursor-pointer text-xs px-2.5 py-0.5"
              onClick={() => {
                setShowCustomOnly(!showCustomOnly);
                setShowCertifiedOnly(false);
              }}
            >
              Personalizzati ({customCount})
            </Badge>
            <Badge
              variant={showFavoritesOnly ? 'default' : 'outline'}
              className="cursor-pointer text-xs px-2.5 py-0.5 gap-1 inline-flex items-center"
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            >
              <Star className={`h-3 w-3 ${showFavoritesOnly ? 'fill-current' : ''}`} />
              Preferiti
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* === GRIGLIA === */}
      {stratigraphies.length === 0 ? (
        <Card className="p-8 text-center border-dashed text-sm text-muted-foreground">
          Nessun risultato per i filtri attuali.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {stratigraphies
            .filter((s) => !showFavoritesOnly || favoritesSet.has(s.id))
            .map((s) => (
              <StratigraphyCardV2
                key={s.id}
                stratigraphy={s}
                onClick={() => openCeiling(s.id)}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default CeilingStratigraphyList;
